import React from "react";
import { Check, X, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

const EMPTY_FORM = { kind: "lesson", year: 1, order: 1, quiz_id: "", title: "", due_at: "" };
export const emptyAssignmentForm = () => ({ ...EMPTY_FORM });

const input = "rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none";

export function AssignmentForm({ form, setForm, quizzes, err, onCreate }) {
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <div className="mt-4 space-y-3 rounded-md border border-white/10 bg-black/60 p-4" data-testid="assignment-form">
      <div className="flex gap-2">
        {["lesson", "custom-quiz"].map((k) => (
          <button key={k} onClick={() => set({ kind: k })} className={`rounded-md border px-3 py-1 text-xs ${form.kind === k ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 bg-white/5 text-slate-300"}`}>
            {k === "lesson" ? "Lesson" : "Quiz"}
          </button>
        ))}
      </div>
      <input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Title (e.g. 'Read atoms chapter by Friday')" data-testid="assignment-title" className={`w-full ${input}`} />
      {form.kind === "lesson" ? (
        <div className="flex gap-2">
          <select value={form.year} onChange={(e) => set({ year: e.target.value })} data-testid="assignment-year" className={input}>
            <option value={1}>Year 1</option><option value={2}>Year 2</option><option value={3}>Year 3</option>
          </select>
          <input type="number" min={1} value={form.order} onChange={(e) => set({ order: e.target.value })} placeholder="Lesson #" data-testid="assignment-order" className={`w-24 ${input}`} />
        </div>
      ) : (
        <select value={form.quiz_id} onChange={(e) => set({ quiz_id: e.target.value })} data-testid="assignment-quiz" className={`w-full ${input}`}>
          <option value="">Select a quiz…</option>
          {quizzes.map((q) => <option key={q.id} value={q.id}>{q.title}</option>)}
        </select>
      )}
      <input type="datetime-local" value={form.due_at} onChange={(e) => set({ due_at: e.target.value })} data-testid="assignment-due" className={`w-full ${input}`} />
      {err && <div className="rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200">{err}</div>}
      <button onClick={onCreate} data-testid="assignment-save" className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-black hover:bg-amber-400">Create</button>
    </div>
  );
}

function CompletionList({ a }) {
  const onTime = a.completions.filter((c) => c.on_time).length;
  return (
    <div className="border-t border-white/5 px-4 py-3 text-xs">
      <div className="mb-2 flex gap-3 text-[10px] uppercase tracking-widest">
        <span className="text-lime-300">On time: {onTime}</span>
        <span className="text-amber-300">Late: {a.completions.length - onTime}</span>
        <span className="text-slate-500">Not yet: {a.class_size - a.completions.length}</span>
      </div>
      {a.completions.length === 0 ? (
        <div className="text-slate-500">Nobody has completed this yet.</div>
      ) : (
        <ul className="space-y-1">
          {a.completions.map((c) => (
            <li key={c.user_id} className="flex items-center justify-between">
              <span className="text-slate-200">{c.name}</span>
              <span className={`font-mono-num text-xs ${c.on_time ? "text-lime-300" : "text-amber-300"}`}>
                {c.on_time ? <Check size={12} className="inline" /> : <X size={12} className="inline" />} {new Date(c.completed_at).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AssignmentRow({ a, isOpen, onToggle, onRemove }) {
  const pct = a.class_size ? Math.round((a.completions.length / a.class_size) * 100) : 0;
  return (
    <div className="rounded-md border border-white/10 bg-black/60" data-testid={`teacher-hw-${a.id}`}>
      <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={(e) => e.key === "Enter" && onToggle()} className="flex w-full cursor-pointer items-center gap-2 px-3 py-3 text-left hover:bg-white/5 sm:gap-3 sm:px-4">
        {isOpen ? <ChevronDown size={12} className="shrink-0 text-slate-400" /> : <ChevronRight size={12} className="shrink-0 text-slate-400" />}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-white">{a.title}</div>
          <div className="truncate text-[10px] uppercase tracking-widest text-slate-500">Due {new Date(a.due_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })} · {a.kind === "lesson" ? `Y${a.year} #${a.order}` : "quiz"}</div>
        </div>
        <span className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-white/5 sm:block"><span className="block h-full bg-lime-400" style={{ width: `${pct}%` }} /></span>
        <span className="font-mono-num shrink-0 text-right text-xs text-lime-200 sm:w-16">{a.completions.length}/{a.class_size}</span>
        <button onClick={(e) => { e.stopPropagation(); onRemove(a.id); }} className="icon-btn shrink-0 p-1.5 text-slate-500 hover:text-red-300" data-testid={`hw-delete-${a.id}`}><Trash2 size={12} /></button>
      </div>
      {isOpen && <CompletionList a={a} />}
    </div>
  );
}
